// Per-country-return TypedInEditor.
// Extracted from CountryReturn.jsx in the Phase-3 refactor.
// All data-testid attributes are preserved verbatim — Playwright
// E2E (`tests/e2e/test_v0465_country_returns_render.py`) and the
// box-level edit tests bind to these IDs.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock } from "@phosphor-icons/react";
import { patchCountryReturnBox } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { useAuth } from "../../auth/AuthContext";
import { ReasonPicker, computeValidity } from "../ReasonPicker";
import {
  TYPED_IN_EDITOR_PRESETS, describePlaceholders,
} from "../reasonPresets";
import { manualOverrideBoxFor } from "../../lib/manualOverrideConfig";

const todayIso = () => new Date().toISOString().slice(0, 10);

// v0.5.9f — deductible-input boxes carry country-specific section names
// (DE/FR/AT/CZ/DK/PT/HU "inputs", ES "iva_deducible", GB "vat_reclaimed").
// The sales-worded presets are a category error on ANY of them, so detect
// the input side by an explicit set + a keyword fallback for future returns.
const INPUT_SECTIONS = new Set(["inputs", "iva_deducible", "vat_reclaimed"]);
const INPUT_KEYWORDS = /deduc|reclaim|input|vorsteuer|detratt/;
const isInputSection = (s) => {
  const sec = (s || "").toLowerCase();
  return INPUT_SECTIONS.has(sec) || INPUT_KEYWORDS.test(sec);
};
// v0.5.9p — a deductible box may live in a generic section (IT LIPE VP5
// 'Deductible VAT', section='vat'); match on the English label too so
// the note-optional / reworded-preset behaviour is consistent with the
// backend _is_input_box() rule. No per-country carve-out.
const isInputBox = (box) =>
  isInputSection(box?.section)
  || INPUT_KEYWORDS.test((box?.label_english || "").toLowerCase());

export const TypedInEditor = ({
  box, onSaved, calcId, country, periodStart, clientCadence,
  moduleIndex, routeDestinationOffenders, calcFrozen, calcSignedOff,
}) => {
  const { admin } = useAuth() || {};
  const { t } = useTranslation();
  // v0.5.7 — Kz 66 (and any deductible-INPUT box) reports VAT on
  // purchases / supplier invoices, not sales. The generic "No sales
  // recorded" zero-value default is a category error there, so reword
  // the input-side presets. Output boxes keep the sales wording.
  const presets = React.useMemo(() => {
    if (!isInputBox(box)) return TYPED_IN_EDITOR_PRESETS;
    return TYPED_IN_EDITOR_PRESETS.map((p) => {
      if (p.id === "no-sales") {
        return {
          ...p,
          label: "No deductible input VAT this period",
          template:
            "No deductible input VAT: no supplier invoices carrying "
            + "recoverable VAT for this reporting period.",
        };
      }
      if (p.id === "manual-calc") {
        return {
          ...p,
          label: "Input VAT exists — manually calculated",
          template:
            "Deductible input VAT exists. Value above is manually "
            + "calculated from supplier invoices; workpaper retained "
            + "at <DOC_REF>.",
        };
      }
      if (p.id === "not-applicable") {
        return {
          ...p,
          label: "No recoverable input VAT for this business",
          template:
            "Entity has no deductible input VAT for this category of "
            + "purchases/inputs this period. Confirmed by <ROLE> on <DATE>.",
        };
      }
      return p;
    }).flatMap((p) => (
      // v0.6.12 (A1-61) — operator-approved wording 2026-10-08,
      // shortened at their request. THE GAP THIS FILLS: A2-204 made the
      // audit note compulsory on all 44 input-side boxes, where it had
      // always been optional. Of the three presets above only the "no
      // input VAT" one is token-free, and it ASSERTS there is no input
      // VAT — useless when the figure is 19,290.73. So for the ordinary
      // case (input VAT exists, taken straight from the ledger) there
      // was no option the operator could complete without inventing a
      // <DOC_REF> workpaper reference, and Save stayed disabled.
      // Reported by the operator within hours of A2-204 landing.
      //
      // Added as a SIBLING of manual-calc rather than by editing it: a
      // workpaper reference is substantive audit content and
      // reasonPresets.js carries a standing "DO NOT edit the templates
      // without explicit Y/N sign-off". manual-calc stays for the case
      // where a workpaper genuinely exists; the two sit side by side so
      // the choice is visible.
      p.id === "manual-calc"
        ? [p, {
            id: "from-ledger",
            label: "From the purchase ledger",
            template: "Taken from the purchase ledger for this period.",
          }]
        : [p]
    ));
  }, [box.section]);
  // v0.4.67/68 — derive numeric constraint + period-aware suggestion
  // from the box's label and the calc's period_start. v0.4.68 layers
  // in `clientCadence` so a MONTHLY-registered IT client only sees
  // a suggestion on VP1_month, never VP1_quarter (and vice versa).
  const label = `${box.label_english || ""} ${box.label_local || ""}`;
  const rangeMatch = label.match(/\((\d+)\.\.(\d+)\)/);
  const minNum = rangeMatch ? parseInt(rangeMatch[1], 10) : null;
  const maxNum = rangeMatch ? parseInt(rangeMatch[2], 10) : null;
  const isNumericConstrained = rangeMatch !== null;
  let suggestion = null;
  let cadenceMismatch = false;
  if (periodStart && isNumericConstrained) {
    const m = parseInt(periodStart.slice(5, 7), 10);
    const isMonthBox = /_month$/.test(box.box_id);
    const isQuarterBox = /_quarter$/.test(box.box_id);
    if (isMonthBox && m >= 1 && m <= 12) {
      if (clientCadence === "QUARTERLY") {
        cadenceMismatch = true;
      } else {
        suggestion = String(m);
      }
    } else if (isQuarterBox) {
      if (clientCadence === "MONTHLY") {
        cadenceMismatch = true;
      } else {
        suggestion = String(Math.floor((m - 1) / 3) + 1);
      }
    }
  }
  const [open, setOpen] = useState(false);
  // v0.5.0d+5 — restore prior save on editor reopen.
  //
  // Previously this editor opened with `value=""` (or the period
  // suggestion) whenever `box.value` was "0"/"0.00", AND `reason`
  // always started "" regardless of whether an override already
  // existed. Operators reading the "override by … at …" stamp at
  // the bottom of the box reasonably believed their prior save
  // had been lost — they had to retype both fields every reopen.
  // The override row was on disk the whole time (backend ships it
  // in `box.override.reason`), the editor just wasn't reading it.
  //
  // Discipline boundary: an existing override means the operator
  // already attested. Reopen pre-fills both fields verbatim so
  // they can REVIEW the prior attestation, then choose to keep,
  // edit, or replace it. We do NOT silently treat 0 as "no value"
  // — if the operator deliberately saved 0 (e.g. no-sales preset)
  // that IS the answer and must round-trip honestly.
  const existingOverride = box.override || null;
  const hasExistingOverride = !!existingOverride?.reason;
  const initialValue = hasExistingOverride
    // Saved override exists — round-trip the persisted value
    // verbatim, including "0" if that's what the operator picked.
    ? String(box.value ?? "")
    : (
      box.value && box.value !== "0" && box.value !== "0.00"
        ? box.value
        : (suggestion || "")
    );
  const [value, setValue] = useState(initialValue);
  const [reason, setReason] = useState(
    hasExistingOverride ? existingOverride.reason : "",
  );
  const [ackConfirmed, setAckConfirmed] = useState(hasExistingOverride);
  // A2-384 (operator check 2026-10-16): a 0 on an input-VAT box means "no
  // deductible input VAT". The operator picked "Input VAT exists — manually
  // calculated" for it, whose <DOC_REF> placeholder kept Save disabled.
  // With an EMPTY note, pre-fill the token-free "no input VAT" wording;
  // never overwrite a note the operator has written or picked.
  React.useEffect(() => {
    if (!isInputBox(box) || reason) return;
    if (String(value ?? "").trim() === "" || Number(value) !== 0) return;
    const none = presets.find((p) => p.id === "no-sales");
    if (none?.template) setReason(none.template);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  // v0.6.8 fix — this editor is keyed by box_id at the call site
  // (CountryReturn.jsx -> Box -> TypedInEditor), so the SAME instance
  // persists across every open/close cycle for a given box; the
  // useState seeds above only ever run once, at mount. Cancel never
  // reset value/reason, and Save only reset reason — so reopening the
  // same box later in the same session (no navigation needed) showed
  // a stale draft or a blank note instead of the real persisted
  // override, directly contradicting this file's own v0.5.0d+5
  // round-trip-on-reopen guarantee (see the comment above
  // `initialValue`). Re-derive from the current box/box.override
  // every time the editor opens.
  React.useEffect(() => {
    if (!open) return;
    const ov = box.override || null;
    const hasOv = !!ov?.reason;
    setValue(hasOv
      ? String(box.value ?? "")
      : (box.value && box.value !== "0" && box.value !== "0.00"
          ? box.value
          : (suggestion || "")));
    setReason(hasOv ? ov.reason : "");
    setAckConfirmed(hasOv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const minChars = Math.max(1, parseInt(box.ack_min_chars || 20, 10));
  const hasWarning = !!box.ack_warning;
  const isNonZero = !!value && value.trim() !== "" && parseFloat(value) !== 0;
  const ackOk = !hasWarning || !isNonZero || ackConfirmed;
  // v0.5.0 — reasonOk delegates to the shared computeValidity
  // helper so it picks up placeholder-token validation (mode a:
  // fail loud — Save blocks until literal `<TOKEN>` placeholders
  // are filled in).
  const reasonValidity = computeValidity(reason, minChars);
  // v0.5.9l — deductible input-VAT boxes are a plain value entry; the
  // operator just types the amount, so the audit note is OPTIONAL here
  // (validated only if they choose to write one, e.g. an unfilled preset
  // placeholder). Output-side / override boxes keep the mandatory note.
  const inputBox = isInputBox(box);
  // v0.6.12 (A2-204) — `editable_reason_required` OVERRIDES the input-box
  // waiver, matching the backend. The flag is declared on 142 boxes and
  // was read by NOTHING at either end, so its own stated guarantee ("PATCH
  // refuses to write a typed-in value without an audit reason") was not
  // provided anywhere. 44 of those 142 sit on the input side and were
  // being waived — NL 5b among them, where the operator saved 19,290.73
  // with an empty reason and that one figure drives the whole return.
  //
  // This does not narrow the v0.5.9p waiver, it makes it unreachable:
  // all 44 typed-in input boxes on the 11 forms carry the flag, so there
  // is no unflagged input box left for it to apply to. Deliberate — the
  // flag predates the waiver by a month (43fb693 2026-06-11 vs 0feb848
  // 2026-07-14) and FR box 20 still says "Audit-logged" from the
  // operator's own sign-off, so the waiver was the regression.
  //
  // The presets shown are unchanged: TypedInEditor already rewords them
  // for the input side, so a flagged input box offers "No deductible
  // input VAT this period" and its siblings, not the sales wording.
  const noteRequired = Boolean(box.editable_reason_required) || !inputBox;
  // An input box where the note is required ONLY because of the flag.
  // Worth saying out loud: every other input box waives the note, so
  // without this the requirement looks like a bug to the operator.
  const flaggedInputBox = inputBox && Boolean(box.editable_reason_required);
  const reasonOk = noteRequired
    ? (reasonValidity.lengthOk && reasonValidity.placeholdersOk)
    : (reason.trim() === "" || reasonValidity.placeholdersOk);
  // v0.4.67 — numeric-range validation. Rejects `0dsds`, `13` when
  // range is 1..12, etc.
  let numericErr = null;
  if (isNumericConstrained && value.trim() !== "") {
    if (!/^\d+$/.test(value.trim())) {
      numericErr = "Digits only";
    } else {
      const n = parseInt(value, 10);
      if (n < minNum || n > maxNum) {
        numericErr = `Must be between ${minNum} and ${maxNum}`;
      }
    }
  }
  // v0.6.6 — ordinary amount boxes (the vast majority of typed-in VAT
  // figures) had NO client-side check at all: `canSave` only required a
  // non-empty string, so "abc" or a malformed value enabled Save and hit
  // the backend's own Decimal validator (routes_country_return.py's
  // BoxPatchBody._v_is_decimal), which safely rejects it with a 422 — no
  // bad data ever lands, but the operator got a raw server error instead
  // of the same instant, specific inline feedback numeric-constrained
  // boxes already have. Mirror the backend's own tolerant parsing here
  // (it accepts a comma decimal separator via `.replace(",", ".")`) so a
  // European-locale "1234,56" is accepted client-side too, not just
  // digits-only.
  let amountErr = null;
  if (!isNumericConstrained && value.trim() !== "") {
    const normalized = value.trim().replace(",", ".");
    if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
      amountErr = "Enter a valid number (e.g. 1234.56 or 1234,56)";
    }
  }
  const canSave = !busy && value.trim() && reasonOk && ackOk && !numericErr && !amountErr;
  // v0.6.6 — normalize a comma decimal separator to a dot BEFORE sending.
  // The backend's own validator accepts either but stores the value
  // VERBATIM once it parses — a saved "1234,56" would round-trip back
  // through display code elsewhere (e.g. Box.jsx's plain `parseFloat`,
  // which is comma-unaware and stops at the first non-numeric char) as
  // the wrong figure "1234". Normalizing here keeps the persisted value
  // in the one canonical dot-decimal form every reader already assumes.
  const submitValue = isNumericConstrained
    ? value.trim()
    : value.trim().replace(",", ".");
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await patchCountryReturnBox(calcId, country, box.box_id,
        { value: submitValue, reason: reason.trim(),
          ...(moduleIndex ? { module_index: moduleIndex } : {}) });
      // v0.5.7 Stage 5 — let the readiness strip re-derive the needs-input gate.
      window.dispatchEvent(new Event("country-return-updated"));
      onSaved?.();
      setOpen(false);
      setReason("");
      setAckConfirmed(false);
    } catch (e) {
      setErr(errorMessage(e));
    } finally { setBusy(false); }
  };
  // v0.6.9 — sweep finding: this was the only editable surface in the
  // whole country-return page with no awareness of the calc's own
  // frozen status. patch_typed_box now refuses the write server-side
  // (the real fix), but the operator deserves to see that BEFORE
  // opening the editor and typing a value that's just going to be
  // rejected — not discover it only from a 409 after Save.
  if (calcFrozen) {
    return (
      <span
        data-testid={`box-edit-locked-${box.box_id}`}
        title={t("ui.country_return.box_edit.locked_title", { defaultValue: "This calculation has been signed off and filed — filed returns are immutable." })}
        className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 cursor-not-allowed"
      >
        <Lock size={11} weight="bold" /> {t("ui.country_return.box_edit.locked", { defaultValue: "filed" })}
      </span>
    );
  }
  // Operator 2026-10-18: "Block edits after sign-off" — the server refuses
  // (api/_signoff_guard); say so here, with the way forward.
  if (calcSignedOff) {
    return (
      <span
        data-testid={`box-edit-signed-off-${box.box_id}`}
        title={t("ui.country_return.box_edit.signed_off_title", { defaultValue: "This calculation is signed off, so its figures are fixed. To change a value, run the calculation again (Step 3) and sign off the new run." })}
        className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 cursor-not-allowed"
      >
        <Lock size={11} weight="bold" /> {t("ui.country_return.box_edit.signed_off", { defaultValue: "signed off" })}
      </span>
    );
  }
  // Operator 2026-10-18 (A6-09): an operator may type the compulsory boxes
  // (the ones sign-off needs); the rest are for an admin. The server
  // refuses the same way (BOX_NEEDS_ADMIN).
  if (admin && admin.role !== "ADMIN" && box.operator_editable === false) {
    return (
      <span
        data-testid={`box-edit-admin-only-${box.box_id}`}
        title={t("ui.country_return.box_edit.admin_only_title", { defaultValue: "Only an admin can change this box. You can enter the boxes this return needs before sign-off." })}
        className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 cursor-not-allowed"
      >
        <Lock size={11} weight="bold" /> {t("ui.country_return.box_edit.admin_only", { defaultValue: "admin only" })}
      </span>
    );
  }
  if (!open) {
    return (
      <button
        data-testid={`box-edit-${box.box_id}`}
        onClick={() => setOpen(true)}
        className="text-xs text-amber-700 dark:text-amber-300 hover:text-amber-700 underline"
      >
        {t("ui.country_return.typed_in.edit", { defaultValue: "edit" })}
      </button>
    );
  }
  return (
    <div className="mt-2 p-3 bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-800 rounded-lg">
      {/* v0.5.0d+7 — Route-flow guidance callout. When this box IS
          the country's manual-override destination AND there's an
          active unknown-rate offender that should be routed here,
          warn the operator BEFORE they save that direct-edit
          stamps the box but does NOT exclude the offender's
          txn_ids from rate_split — the unknown-rate guard will
          still refuse to file. The Inspect → Route flow on the
          offender row IS the right path. Closes the operator-
          reported confusion of saving Box 35 directly and
          wondering why DE was still blocked. */}
      {(() => {
        const isMOBox = manualOverrideBoxFor(country) === box.box_id;
        const offCount = (routeDestinationOffenders || []).length;
        if (!isMOBox || offCount === 0) return null;
        return (
          <div
            data-testid={`box-edit-route-warning-${box.box_id}`}
            className="mb-3 px-2 py-2 bg-rose-50 dark:bg-rose-950/40 border border-rose-400 dark:border-rose-700 rounded text-[11px] text-rose-900 dark:text-rose-100 leading-snug"
          >
            <span className="font-bold">⚠ Editing this box directly won&apos;t unblock the filing.</span>{/* i18n-ignore: pinned by test_v050d7 */}
            <div className="mt-1 opacity-90">
              {offCount} flagged row{offCount === 1 ? "" : "s"} at unrecognised rate{offCount === 1 ? "" : "s"} should
              be assigned to Box {box.box_id}. Entering a value here directly stamps the box but leaves the
              flagged row{offCount === 1 ? "" : "s"} in the engine&apos;s per-rate breakdown — the unknown-rate check
              will still refuse to file.
            </div>
            <div className="mt-1 opacity-90">
              <span className="font-bold">Do this instead:</span>{/* i18n-ignore: block pinned by test_v050d7 */}
              {" "}scroll up to the flagged-rows table → click
              <span className="font-bold"> Review {offCount} transaction{offCount === 1 ? "" : "s"} </span>{/* i18n-ignore: pinned by test_v050d7 */}
              on the row → click
              <span className="font-bold"> Assign all → {country} Box {box.box_id}</span>.
              That removes the rows from the per-rate breakdown AND stamps this box in one step.{/* i18n-ignore: pinned by test_v050d7 */}
            </div>
          </div>
        );
      })()}
      {/* v0.5.0d+6 — Visible "you're reviewing a prior save"
          header. Without it, the editor opening with the prior
          value + reason looks indistinguishable from a fresh
          blank form: the operator sees populated fields and
          reasonably believes they're being asked to re-enter
          data. The header makes the round-trip from v0.5.0d+5
          explicit, not implicit. */}
      {hasExistingOverride && (
        <div
          data-testid={`box-edit-prior-save-${box.box_id}`}
          className="mb-3 px-2 py-1.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-700 rounded text-[11px] text-emerald-900 dark:text-emerald-100 leading-snug"
        >
          <span className="font-bold">✓ You&apos;ve already saved a value here</span>{/* i18n-ignore: pinned by test_v050d6 */}
          {" · "}
          {existingOverride.by || t("ui.country_return.typed_in.operator_fallback", { defaultValue: "operator" })}
          {" · "}
          {(existingOverride.at || "").slice(0, 16).replace("T", " ")}
          <div className="text-emerald-800 dark:text-emerald-200 opacity-80 mt-0.5">
            {t("ui.country_return.typed_in.loaded_note", { defaultValue: "The value and note below are loaded from your last save. Edit and Save to replace, or Cancel to keep them as-is." })}
          </div>
        </div>
      )}
      {hasWarning && isNonZero && (
        <div
          data-testid={`box-ack-warning-${box.box_id}`}
          className="mb-2 p-2 bg-amber-50 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-700 rounded text-[11px] text-amber-900 dark:text-amber-100 leading-snug"
        >
          <div className="font-bold text-amber-700 dark:text-amber-300 mb-1">{t("ui.country_return.box_edit.ack_title", { defaultValue: "⚠ Please confirm this applies" })}</div>
          {box.ack_warning}
          <label className="flex items-start gap-2 mt-2 cursor-pointer">
            <input
              data-testid={`box-ack-checkbox-${box.box_id}`}
              type="checkbox"
              checked={ackConfirmed}
              onChange={(e) => setAckConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>{t("ui.country_return.box_edit.ack_confirm", { defaultValue: "I confirm this applies to my business and the note below records my reasoning." })}</span>
          </label>
        </div>
      )}
      {/* v0.4.67 — explicit label + placeholder so the operator
          doesn't have to guess what goes in the value field. The
          box's label_english carries the constraint (e.g.
          "Period — quarter (1..5)"). */}
      <label className="block text-[11px] tracking-normal text-amber-700 dark:text-amber-300 mb-1">
        {t("ui.country_return.typed_in.value_label", { defaultValue: "Value —" })} {box.label_english || box.label_local || box.box_id}
        {suggestion && (
          <span data-testid={`box-edit-suggestion-${box.box_id}`} className="ml-2 text-amber-700 dark:text-amber-300 normal-case">
            {t("ui.country_return.typed_in.suggestion", { defaultValue: "(suggestion for this period:" })} <b>{suggestion}</b>)
          </span>
        )}
        {cadenceMismatch && (
          <span data-testid={`box-edit-cadence-mismatch-${box.box_id}`} className="ml-2 text-rose-700 dark:text-rose-300 normal-case">
            {t("ui.country_return.typed_in.cadence_mismatch", { cadence: clientCadence, defaultValue: `(client cadence is ${clientCadence} — this box is the alternative; only fill if overriding)` })}
          </span>
        )}
      </label>
      <input
        data-testid={`box-edit-value-${box.box_id}`}
        value={value}
        onChange={(e) => {
          // v0.4.67 — strip non-digit chars on numeric-constrained
          // boxes so the operator can't type "0dsds" at all.
          const raw = e.target.value;
          setValue(isNumericConstrained ? raw.replace(/\D/g, "") : raw);
        }}
        inputMode={isNumericConstrained ? "numeric" : "text"}
        pattern={isNumericConstrained ? "\\d*" : undefined}
        placeholder={
          isNumericConstrained
            ? (suggestion
                ? `${suggestion}  (period suggestion — replace if different)`
                : `Integer ${minNum}–${maxNum}`)
            : `e.g. 0 or a number / amount per the box constraint above`
        }
        className={`w-full bg-white dark:bg-slate-800 border rounded px-2 py-1 tabular-nums text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 mb-1 ${
          numericErr || amountErr ? "border-rose-500" : "border-slate-300 dark:border-slate-700"
        }`}
      />
      {numericErr && (
        <div data-testid={`box-edit-numeric-err-${box.box_id}`} className="text-[11px] text-rose-700 dark:text-rose-300 mb-2">
          {numericErr}
        </div>
      )}
      {amountErr && (
        <div data-testid={`box-edit-amount-err-${box.box_id}`} className="text-[11px] text-rose-700 dark:text-rose-300 mb-2">
          {amountErr}
        </div>
      )}
      {!numericErr && !amountErr && <div className="mb-2" />}
      <div className="border-t border-slate-200 dark:border-slate-700 my-3" />
      <label className="block text-[11px] tracking-normal text-amber-700 dark:text-amber-300 mb-2">
        {noteRequired
          ? t("ui.country_return.typed_in.note_required", { n: minChars, defaultValue: `Audit note (minimum ${minChars} characters — saved into the exported spreadsheet)` })
          : t("ui.country_return.typed_in.note_optional", { defaultValue: "Audit note (optional — e.g. where this figure came from)" })}
      </label>
      {/* v0.5.0 — ReasonPicker replaces the bare textarea. Operator
          can pick a preset, edit the populated template (the
          `<TOKEN>` placeholders the picker validates against),
          or click "Other (explain)" for a clean text box. */}
      <ReasonPicker
        testid={`box-edit-reason-${box.box_id}`}
        value={reason}
        onChange={setReason}
        presets={presets}
        minChars={minChars}
        /* v0.5.0c+4 — `country` / `onDate` no longer passed.
           After dropping `<RATE_DESC>` from no-sales /
           manual-calc / not-applicable (round-D), NO typed-in
           editor preset references a rate token any more, so
           the RateTokenFiller would just be dormant UI in this
           surface. The filler component remains in use by
           OffenderInspectDrawer where the rate IS the operative
           claim (drift-flag notes, supersede reasons). */
        onPresetPicked={(p) => {
          // v0.5.0c+3/+5 — operator round-D coordination. Cuts
          // friction on point-in-time attestations by auto-
          // filling tokens that match what the system already
          // knows (logged-in user identity, today's date).
          //
          // Discipline boundary (Q2 sign-off 2026-06-20): we do
          // NOT auto-substitute `<DATE>` on `from-accountant` —
          // there, `<DATE>` is when the accountant PROVIDED the
          // value, not when the operator clicks the preset.
          // Stamping today would silently put a false claim
          // ("received from accountant today") into the audit
          // trail. Same discipline as the `<RATE_DESC>` purge.
          if (p.id === "no-sales" && !value.trim()) {
            setValue("0");
          }
          if (p.id === "not-applicable") {
            // (a) Same amount=0 default as no-sales.
            if (!value.trim()) setValue("0");
            // (b/c) Auto-fill the DATE token with today + the
            // ROLE token with the logged-in user's name+role.
            // The operator picking this preset IS the moment of
            // attestation.
            const name = admin?.display_name
              || admin?.email?.split("@")[0]
              || "operator";
            const role = admin?.role || "OPERATOR";
            const filled = (p.template || "")
              .replace("<DATE>", todayIso())
              .replace("<ROLE>", `${name} (${role})`);
            setReason(filled);
          }
          // A2 rot triage 2026-10-19: a v0.6 change filled <DATE> with today
          // on "from-accountant" too, against the sign-off above — <DATE>
          // there is when the accountant PROVIDED the value. Removed: the
          // operator types that date (the picker lists the unfilled token),
          // and <ACCOUNTANT_NAME> was never fillable anyway.
        }}
        exampleAnswer={
          // Operator check 2026-10-16: this Italian filing-period example
          // showed on every box (German Kz 66, UK Box 4). Only the
          // period-number boxes it was written for get it.
          !isNumericConstrained ? undefined
            : hasWarning
              ? `Cliente registrato in regime trimestrale ai sensi dell'art. 7 DPR 542/1999 — confermato dal commercialista 2026-06-14`
              : `Monthly filer per Italian VAT registration profile, confirmed by accountant 2026-06-14`
        }
      />
      {isInputBox(box) && String(value ?? "").trim() !== "" && Number(value) === 0
        && /input VAT exists/i.test(reason || "") && (
        <div data-testid={`box-edit-zero-hint-${box.box_id}`} className="text-xs text-amber-700 dark:text-amber-300 mt-1">
          {t("ui.country_return.typed_in.zero_hint", { defaultValue: "You entered 0 — \"No deductible input VAT this period\" fits a zero better than \"Input VAT exists\"." })}
        </div>
      )}
      {err && <div className="text-xs text-rose-700 dark:text-rose-300 mt-1">{err}</div>}
      {!canSave && !err && (
        <div
          data-testid={`box-edit-blockers-${box.box_id}`}
          className="text-[11px] text-rose-700 dark:text-rose-300 mt-2 leading-snug"
        >
          {t("ui.country_return.box_edit.save_disabled", { defaultValue: "Save disabled until:" })}{!value.trim() && <div>· {t("ui.country_return.box_edit.need_value", { defaultValue: "enter a value" })}</div>}
          {amountErr && <div>· {amountErr}</div>}
          {/* v0.6.12 (A2-204) — reports the ACTUAL blocker rather than
              guessing from the box kind. The old line branched on
              `inputBox`, so a flagged input box was told to "fill in the
              placeholders, or clear it" when the real cause was the
              minimum length; and on an output box with an unfilled token
              but enough characters it said "at least N characters" when
              the real cause was the token. Both conditions are part of
              `reasonOk`, so both are listed, each only when it applies —
              a disabled control stating the wrong reason is the one
              thing a blocker line must never do. */}
          {/* v0.6.12 (A1-61) — names what is wanted, not the token.
              This line had the same engine-vocabulary problem as
              ReasonPicker's counter: it printed the raw <DOC_REF>. */}
          {!reasonOk && !reasonValidity.placeholdersOk && (
            <div>{t("ui.country_return.typed_in.still_to_fill", { defaultValue: "· still to fill in:" })}{" "}
              {describePlaceholders(reasonValidity.remaining).join(", ")}
              {noteRequired ? "" : t("ui.country_return.typed_in.or_clear_note", { defaultValue: " (or clear the note)" })}</div>
          )}
          {!reasonOk && reasonValidity.placeholdersOk && !reasonValidity.lengthOk && (
            <div>{t("ui.country_return.typed_in.note_min", { n: minChars, defaultValue: `· audit note is at least ${minChars} characters` })}
              {flaggedInputBox ? t("ui.country_return.typed_in.box_requires_note", { defaultValue: " — this box requires one" }) : ""}</div>
          )}
          {hasWarning && isNonZero && !ackConfirmed && (
            <div>· {t("ui.country_return.box_edit.need_ack", { defaultValue: "tick the acknowledgement above" })}</div>
          )}
        </div>
      )}
      <div className="flex gap-2 mt-2">
        <button
          data-testid={`box-edit-cancel-${box.box_id}`}
          onClick={() => { setOpen(false); setErr(null); setAckConfirmed(false); }}
          className="text-xs border border-slate-300 dark:border-slate-700 px-2 py-1 rounded text-slate-500 dark:text-slate-400"
        >{t("ui.country_return.typed_in.cancel", { defaultValue: "Cancel" })}</button>
        <button
          data-testid={`box-edit-save-${box.box_id}`}
          onClick={save} disabled={!canSave}
          className={`text-xs px-2 py-1 rounded font-semibold ${
            canSave ? "bg-amber-400 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 cursor-not-allowed"
          }`}
        >{t("ui.country_return.typed_in.save", { defaultValue: "Save" })}</button>
      </div>
    </div>
  );
};
